import React from "react";
import { Link } from "react-router-dom";
import {
    Loader2,
    Wrench,
    Globe,
    ShieldCheck,
    AlertTriangle,
    Network,
    ExternalLink,
    CheckCircle2,
    XCircle,
    ShieldAlert,
    Server,
    Building2,
    Activity,
    LogIn,
    Sparkles,
    Lock,
    Zap,
    Radar,
} from "lucide-react";

/**
 * <ToolCallCard /> — inline tool execution card rendered within an assistant
 * message stream. Mirrors the SSE protocol fields `tool_call` + `tool_result`.
 *
 * Props:
 *   name        — function name as the LLM called it
 *   args        — JSON args the LLM passed
 *   status      — "running" | "success" | "error"
 *   result      — the tool output dict (when status === "success")
 *   error       — the error string (when status === "error")
 *   errorCode   — AUTH_REQUIRED | UPGRADE_REQUIRED | TIMEOUT | … (Phase D.2)
 *   userMessage — human-friendly hint surfaced by the dispatcher (Phase D.2)
 *   durationMs  — execution time
 */
export const ToolCallCard = ({
    name,
    args,
    status,
    result,
    error,
    errorCode,
    userMessage,
    redirectUrl,
    durationMs,
}) => {
    const isRunning = status === "running";
    const isError = status === "error";
    const isAuthGate = isError && errorCode === "AUTH_REQUIRED";
    const isUpgradeGate = isError && errorCode === "UPGRADE_REQUIRED";
    const isRedirect = isError && errorCode === "DETERMINISTIC_REDIRECT";

    const ToolIcon = TOOL_ICONS[name] || Wrench;
    const friendlyName = TOOL_LABELS[name] || name;

    // Pretty banner-style cards for the gating outcomes — they're not really
    // errors, they're CTAs the LLM hands off to the UI.
    if (isAuthGate) {
        return <AuthRequiredCard name={name} userMessage={userMessage} friendlyName={friendlyName} />;
    }
    if (isUpgradeGate) {
        return <UpgradeRequiredCard name={name} userMessage={userMessage} friendlyName={friendlyName} />;
    }
    if (isRedirect) {
        return (
            <DeterministicRedirectCard
                name={name}
                userMessage={userMessage}
                friendlyName={friendlyName}
                redirectUrl={redirectUrl || (typeof userMessage === "string" && userMessage.match(/\/tools\/[a-z0-9-]+/)?.[0]) || "/tools"}
            />
        );
    }

    return (
        <div
            className={`my-2 rounded-lg border ${
                isError
                    ? "border-accent-red/30 bg-accent-red/[0.04]"
                    : "border-white/[0.06] bg-elevated/40"
            } overflow-hidden`}
            data-testid={`tool-card-${name}`}
        >
            <header className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/[0.04]">
                <div className="inline-flex items-center gap-2 min-w-0">
                    {isRunning ? (
                        <Loader2 className="w-3.5 h-3.5 text-accent-cyan animate-spin shrink-0" />
                    ) : isError ? (
                        <XCircle className="w-3.5 h-3.5 text-accent-red shrink-0" />
                    ) : (
                        <CheckCircle2 className="w-3.5 h-3.5 text-accent-mint shrink-0" />
                    )}
                    <ToolIcon className="w-3.5 h-3.5 text-accent-cyan shrink-0" />
                    <span className="text-[11.5px] font-mono uppercase tracking-wider text-text-muted truncate">
                        {friendlyName}
                    </span>
                </div>
                {!isRunning && typeof durationMs === "number" && (
                    <span className="text-[10.5px] font-mono text-text-dim shrink-0">
                        {durationMs} ms
                    </span>
                )}
            </header>
            <div className="px-3 py-2">
                {/* args summary always shown */}
                {Object.keys(args || {}).length > 0 && (
                    <div className="text-[11.5px] font-mono text-text-dim flex flex-wrap gap-x-3 gap-y-0.5 mb-2">
                        {Object.entries(args || {}).map(([k, v]) => (
                            <span key={k}>
                                <span className="text-text-dim/70">{k}=</span>
                                <span className="text-text-muted">{String(v)}</span>
                            </span>
                        ))}
                    </div>
                )}

                {isRunning && (
                    <p className="text-[12px] text-text-muted">Running…</p>
                )}
                {isError && (
                    <p
                        className="text-[12px] text-accent-red font-mono"
                        data-testid={`tool-error-${name}`}
                    >
                        {error || "Tool failed."}
                    </p>
                )}
                {!isRunning && !isError && (
                    <ResultBody name={name} result={result} />
                )}
            </div>
        </div>
    );
};

// ---- Auth gate banner ---------------------------------------------------- //

const AuthRequiredCard = ({ name, userMessage, friendlyName }) => {
    // Encode the current path so post-login we land back where the user was.
    const next =
        typeof window !== "undefined"
            ? encodeURIComponent(window.location.pathname + window.location.search)
            : "/";
    return (
        <div
            className="my-2 rounded-lg border border-accent-cyan/30 bg-accent-cyan/[0.05] overflow-hidden"
            data-testid={`tool-card-auth-required-${name}`}
        >
            <header className="flex items-center gap-2 px-3 py-2 border-b border-accent-cyan/15">
                <Lock className="w-3.5 h-3.5 text-accent-cyan shrink-0" />
                <span className="text-[11.5px] font-mono uppercase tracking-wider text-accent-cyan truncate">
                    Sign-in required · {friendlyName}
                </span>
            </header>
            <div className="px-3 py-3">
                <p className="text-[12.5px] text-text-primary leading-relaxed mb-3">
                    {userMessage ||
                        "I need to access your account data for that. Sign in to your Cybersecurity Forefront workspace and try again."}
                </p>
                <Link
                    to={`/login?next=${next}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-mono px-3 py-1.5 rounded-md bg-accent-cyan text-canvas hover:shadow-glow-cyan transition-shadow duration-200"
                    data-testid={`tool-cta-signin-${name}`}
                >
                    <LogIn className="w-3.5 h-3.5" />
                    Sign in
                </Link>
            </div>
        </div>
    );
};

// ---- Deterministic-redirect banner --------------------------------------- //

const DeterministicRedirectCard = ({ name, userMessage, friendlyName, redirectUrl }) => {
    const url = redirectUrl || "/tools";
    return (
        <div
            className="my-2 rounded-lg border border-accent-mint/30 bg-accent-mint/[0.04] overflow-hidden"
            data-testid={`tool-card-redirect-${name}`}
        >
            <header className="flex items-center gap-2 px-3 py-2 border-b border-accent-mint/15">
                <Zap className="w-3.5 h-3.5 text-accent-mint shrink-0" />
                <span className="text-[11.5px] font-mono uppercase tracking-wider text-accent-mint truncate">
                    Faster on the standalone page · {friendlyName}
                </span>
            </header>
            <div className="px-3 py-3">
                <p className="text-[12.5px] text-text-primary leading-relaxed mb-3">
                    {userMessage ||
                        "You've already run this calculator in this chat. The dedicated tool page is instant and doesn't burn LLM tokens — open it for repeated calculations."}
                </p>
                <Link
                    to={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[12px] font-mono px-3 py-1.5 rounded-md bg-accent-mint text-canvas hover:shadow-glow-mint transition-shadow duration-200"
                    data-testid={`tool-cta-redirect-${name}`}
                >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open {url}
                </Link>
            </div>
        </div>
    );
};

// ---- Upgrade gate banner ------------------------------------------------- //

const UpgradeRequiredCard = ({ name, userMessage, friendlyName }) => {
    return (
        <div
            className="my-2 rounded-lg border border-accent-amber/30 bg-accent-amber/[0.05] overflow-hidden"
            data-testid={`tool-card-upgrade-required-${name}`}
        >
            <header className="flex items-center gap-2 px-3 py-2 border-b border-accent-amber/15">
                <Sparkles className="w-3.5 h-3.5 text-accent-amber shrink-0" />
                <span className="text-[11.5px] font-mono uppercase tracking-wider text-accent-amber truncate">
                    Pro feature · {friendlyName}
                </span>
            </header>
            <div className="px-3 py-3">
                <p className="text-[12.5px] text-text-primary leading-relaxed mb-3">
                    {userMessage ||
                        "This is a Pro feature. Upgrade to Pro ($19.90/month) to query your IPAM and CVE alerts from the chat."}
                </p>
                <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1.5 text-[12px] font-mono px-3 py-1.5 rounded-md bg-accent-amber text-canvas hover:shadow-glow-amber transition-shadow duration-200"
                    data-testid={`tool-cta-upgrade-${name}`}
                >
                    <Sparkles className="w-3.5 h-3.5" />
                    View plans
                </Link>
            </div>
        </div>
    );
};

const TOOL_ICONS = {
    // Public
    subnet_calculator: Network,
    dns_health_check: Globe,
    cve_lookup: ShieldCheck,
    // Auth-gated tenant tools (Phase D.2)
    list_my_cve_alerts: ShieldAlert,
    acknowledge_cve_alert: CheckCircle2,
    search_my_subnets: Network,
    search_my_hardware: Server,
    list_my_clients: Building2,
    get_my_cve_alerts_stats: Activity,
    // Phase E
    public_audit: Radar,
};

const TOOL_LABELS = {
    // Public
    subnet_calculator: "Subnet calculator",
    dns_health_check: "DNS health check",
    cve_lookup: "CVE lookup",
    // Auth-gated tenant tools (Phase D.2)
    list_my_cve_alerts: "My CVE alerts",
    acknowledge_cve_alert: "Acknowledge CVE alert",
    search_my_subnets: "My subnets",
    search_my_hardware: "My hardware",
    list_my_clients: "My clients",
    get_my_cve_alerts_stats: "CVE posture overview",
    // Phase E
    public_audit: "Public Surface Audit",
};

const ResultBody = ({ name, result }) => {
    if (!result) return null;
    // Public
    if (name === "subnet_calculator") return <SubnetResult r={result} />;
    if (name === "dns_health_check") return <DnsResult r={result} />;
    if (name === "cve_lookup") return <CveResult r={result} />;
    // Phase D.2 — auth-gated tenant tools
    if (name === "list_my_cve_alerts") return <MyCveAlertsResult r={result} />;
    if (name === "acknowledge_cve_alert") return <AcknowledgeAlertResult r={result} />;
    if (name === "search_my_subnets") return <MySubnetsResult r={result} />;
    if (name === "search_my_hardware") return <MyHardwareResult r={result} />;
    if (name === "list_my_clients") return <MyClientsResult r={result} />;
    if (name === "get_my_cve_alerts_stats") return <MyAlertStatsResult r={result} />;
    if (name === "public_audit") return <PublicAuditResult r={result} />;
    return (
        <pre className="text-[11.5px] font-mono text-text-muted whitespace-pre-wrap break-all">
            {JSON.stringify(result, null, 2)}
        </pre>
    );
};

const Row = ({ k, v, mono = true }) => (
    <>
        <dt className="text-text-dim">{k}</dt>
        <dd
            className={`${
                mono ? "font-mono" : ""
            } text-text-primary break-all`}
        >
            {v ?? "—"}
        </dd>
    </>
);

const SubnetResult = ({ r }) => (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12.5px]">
        <Row k="Network" v={r.network} />
        <Row k="Broadcast" v={r.broadcast} />
        <Row k="Netmask" v={r.netmask} />
        <Row k="Wildcard" v={r.wildcard_mask} />
        <Row k="Hosts" v={`${r.usable_hosts} (${r.first_usable_host} – ${r.last_usable_host})`} />
        <Row k="Gateway" v={r.suggested_gateway} />
        <Row k="Class" v={`${r.address_class}${r.is_private ? " (private)" : ""}`} mono={false} />
    </dl>
);

const DnsResult = ({ r }) => (
    <div className="space-y-2 text-[12.5px]">
        <div className="flex items-center justify-between">
            <span className="text-text-dim">Domain</span>
            <span className="font-mono text-text-primary">{r.domain}</span>
        </div>
        <div className="flex items-center justify-between">
            <span className="text-text-dim">Score</span>
            <span
                className={`font-mono ${
                    r.score >= 80
                        ? "text-accent-mint"
                        : r.score >= 60
                        ? "text-accent-amber"
                        : "text-accent-red"
                }`}
            >
                {r.score}/100
            </span>
        </div>
        {[
            ["A", r.a],
            ["AAAA", r.aaaa],
            ["MX", r.mx],
            ["NS", r.ns],
            ["SPF", r.spf],
            ["DMARC", r.dmarc],
        ]
            .filter(([_, arr]) => Array.isArray(arr) && arr.length > 0)
            .map(([k, arr]) => (
                <div key={k}>
                    <p className="text-text-dim text-[11.5px] uppercase font-mono tracking-wider">
                        {k}
                    </p>
                    <ul className="font-mono text-text-primary text-[12px] space-y-0.5">
                        {arr.slice(0, 5).map((v, i) => (
                            <li key={i} className="break-all">
                                {v}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        {r.warnings?.length > 0 && (
            <div className="rounded border border-accent-amber/30 bg-accent-amber/5 p-2">
                <p className="text-accent-amber text-[11.5px] inline-flex items-center gap-1 mb-1 font-mono uppercase tracking-wider">
                    <AlertTriangle className="w-3 h-3" /> Warnings
                </p>
                <ul className="text-[12px] text-text-muted space-y-0.5">
                    {r.warnings.map((w, i) => (
                        <li key={i}>· {w}</li>
                    ))}
                </ul>
            </div>
        )}
    </div>
);

const CveResult = ({ r }) => {
    if (!r.items?.length) {
        return (
            <p className="text-[12px] text-text-muted">
                No CVE matches for <span className="font-mono">{r.query}</span>.
            </p>
        );
    }
    return (
        <div className="space-y-2">
            {r.items.map((c) => (
                <CveItemCard key={c.cve_id} c={c} />
            ))}
        </div>
    );
};

// Shared severity → pill style helper (used by public + tenant CVE cards).
const sevPill = (severity) => {
    if (severity === "Critical")
        return "bg-accent-red/15 text-accent-red border-accent-red/40";
    if (severity === "High")
        return "bg-accent-amber/15 text-accent-amber border-accent-amber/40";
    if (severity === "Medium")
        return "bg-yellow-500/15 text-yellow-400 border-yellow-500/30";
    return "bg-zinc-700/30 text-text-muted border-white/10";
};

const CveItemCard = ({ c }) => (
    <div className="rounded border border-white/[0.06] p-2">
        <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="font-mono text-accent-cyan text-[12.5px]">
                {c.cve_id}
            </span>
            <span
                className={`text-[10.5px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${sevPill(c.cvss_severity)}`}
            >
                {c.cvss_severity} {c.cvss_score?.toFixed?.(1)}
            </span>
        </div>
        <p className="text-[12px] text-text-muted leading-snug line-clamp-3">
            {c.summary}
        </p>
        {c.references?.[0] && (
            <a
                href={c.references[0]}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-1.5 text-[11.5px] text-accent-cyan hover:underline"
            >
                Vendor advisory <ExternalLink className="w-2.5 h-2.5" />
            </a>
        )}
    </div>
);

// ============== Phase D.2 — auth-gated tool result renderers =============== //

const EmptyState = ({ label }) => (
    <p className="text-[12px] text-text-muted">{label}</p>
);

const ResultHeader = ({ left, right }) => (
    <div className="flex items-center justify-between text-[11px] font-mono text-text-dim mb-2">
        <span>{left}</span>
        {right ? <span>{right}</span> : null}
    </div>
);

// list_my_cve_alerts result
const MyCveAlertsResult = ({ r }) => {
    if (!r?.items?.length) {
        return <EmptyState label="No CVE alerts match those filters in your tenant." />;
    }
    return (
        <div className="space-y-2" data-testid="result-list-my-cve-alerts">
            <ResultHeader
                left={`${r.returned} of ${r.total} alert${r.total === 1 ? "" : "s"}`}
                right={
                    r.filters?.status
                        ? `status=${r.filters.status}${
                              r.filters.severity && r.filters.severity !== "all"
                                  ? ` · ${r.filters.severity}`
                                  : ""
                          }`
                        : null
                }
            />
            {r.items.map((a) => (
                <div
                    key={a.id}
                    className="rounded border border-white/[0.06] p-2"
                    data-testid={`alert-row-${a.id}`}
                >
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-mono text-accent-cyan text-[12.5px]">
                            {a.cve_id}
                        </span>
                        <span
                            className={`text-[10.5px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${sevPill(a.cvss_severity)}`}
                        >
                            {a.cvss_severity} {a.cvss_score?.toFixed?.(1)}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11.5px] font-mono text-text-dim mb-1">
                        <span>
                            {a.hardware
                                ? `${a.hardware.vendor || ""} ${a.hardware.model || ""}${
                                      a.hardware.hostname ? ` · ${a.hardware.hostname}` : ""
                                  }`
                                : "—"}
                        </span>
                        {a.client?.name && (
                            <span className="text-text-muted">@ {a.client.name}</span>
                        )}
                    </div>
                    {a.summary && (
                        <p className="text-[12px] text-text-muted leading-snug line-clamp-2">
                            {a.summary}
                        </p>
                    )}
                    <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">
                            status: {a.status}
                        </span>
                        <Link
                            to={`/alerts/${a.id}`}
                            className="text-[11.5px] text-accent-cyan hover:underline inline-flex items-center gap-1"
                            data-testid={`alert-open-${a.id}`}
                        >
                            Open <ExternalLink className="w-2.5 h-2.5" />
                        </Link>
                    </div>
                </div>
            ))}
        </div>
    );
};

// acknowledge_cve_alert result
const AcknowledgeAlertResult = ({ r }) => (
    <div
        className="text-[12.5px] space-y-1"
        data-testid="result-acknowledge-cve-alert"
    >
        <div className="inline-flex items-center gap-1.5 text-accent-mint">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span className="font-mono">{r.cve_id}</span>
            <span className="text-text-muted">
                {r.previous_status} → {r.new_status}
            </span>
        </div>
        <p className="text-[11.5px] font-mono text-text-dim">
            Alert id: <span className="text-text-muted">{r.id}</span>
        </p>
    </div>
);

// search_my_subnets result
const MySubnetsResult = ({ r }) => {
    if (!r?.items?.length) {
        return <EmptyState label="No subnets match in your tenant." />;
    }
    return (
        <div className="space-y-1.5" data-testid="result-search-my-subnets">
            <ResultHeader left={`${r.returned} of ${r.total} subnet${r.total === 1 ? "" : "s"}`} />
            {r.items.map((s) => (
                <div
                    key={s.id}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded border border-white/[0.06] px-2 py-1.5"
                    data-testid={`subnet-row-${s.id}`}
                >
                    <span className="font-mono text-accent-cyan text-[12px]">
                        {s.cidr}
                    </span>
                    <span className="text-[12px] text-text-primary truncate">
                        {s.name || "—"}
                        {s.vlan_id != null && (
                            <span className="ml-2 text-text-dim font-mono">
                                VLAN {s.vlan_id}
                            </span>
                        )}
                    </span>
                    <span className="text-[11px] font-mono text-text-dim">
                        {s.client?.name || ""}
                    </span>
                </div>
            ))}
        </div>
    );
};

// search_my_hardware result
const MyHardwareResult = ({ r }) => {
    if (!r?.items?.length) {
        return <EmptyState label="No hardware matches in your tenant." />;
    }
    return (
        <div className="space-y-1.5" data-testid="result-search-my-hardware">
            <ResultHeader left={`${r.returned} of ${r.total} asset${r.total === 1 ? "" : "s"}`} />
            {r.items.map((h) => (
                <div
                    key={h.id}
                    className="rounded border border-white/[0.06] px-2 py-1.5"
                    data-testid={`hardware-row-${h.id}`}
                >
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-accent-cyan text-[12px] truncate">
                            {h.vendor} {h.model}
                        </span>
                        <span className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim shrink-0">
                            {h.category}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11.5px] font-mono text-text-dim mt-0.5">
                        {h.hostname && <span>{h.hostname}</span>}
                        {h.ip_address && <span>{h.ip_address}</span>}
                        {h.client?.name && (
                            <span className="text-text-muted truncate">@ {h.client.name}</span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

// list_my_clients result
const MyClientsResult = ({ r }) => {
    if (!r?.items?.length) {
        return <EmptyState label="No clients in your tenant yet — try /clients to add one." />;
    }
    return (
        <div className="space-y-1.5" data-testid="result-list-my-clients">
            <ResultHeader left={`${r.returned} of ${r.total} client${r.total === 1 ? "" : "s"}`} />
            {r.items.map((c) => (
                <Link
                    key={c.id}
                    to={`/clients/${c.slug || c.id}`}
                    className="block rounded border border-white/[0.06] px-2 py-1.5 hover:border-accent-cyan/30 transition-colors"
                    data-testid={`client-row-${c.id}`}
                >
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[12.5px] text-text-primary truncate">
                            {c.name}
                        </span>
                        <span className="text-[10.5px] font-mono text-text-dim shrink-0">
                            {c.hardware_count} hw · {c.subnet_count} sn
                            {c.new_cve_alerts > 0 && (
                                <span className="ml-1.5 text-accent-amber">
                                    · {c.new_cve_alerts} new alert
                                    {c.new_cve_alerts === 1 ? "" : "s"}
                                </span>
                            )}
                        </span>
                    </div>
                    {c.industry && (
                        <span className="text-[11px] font-mono text-text-dim">
                            {c.industry}
                            {c.nis2_in_scope ? " · NIS2" : ""}
                        </span>
                    )}
                </Link>
            ))}
        </div>
    );
};

// get_my_cve_alerts_stats result
const MyAlertStatsResult = ({ r }) => (
    <div className="space-y-2" data-testid="result-my-cve-alerts-stats">
        <div className="grid grid-cols-5 gap-2">
            {["Critical", "High", "Medium", "Low", "None"].map((sev) => (
                <div
                    key={sev}
                    className={`rounded border px-2 py-1.5 text-center ${sevPill(sev)}`}
                >
                    <div className="text-[11px] font-mono uppercase tracking-wider opacity-75">
                        {sev}
                    </div>
                    <div className="text-[14px] font-mono">
                        {r.by_severity?.[sev] ?? 0}
                    </div>
                </div>
            ))}
        </div>
        <div className="text-[11.5px] font-mono text-text-dim flex flex-wrap gap-x-3 gap-y-0.5">
            <span>
                total new=
                <span className="text-text-muted">{r.total_new}</span>
            </span>
            {r.by_status &&
                Object.entries(r.by_status).map(([k, v]) => (
                    <span key={k}>
                        {k}=<span className="text-text-muted">{v}</span>
                    </span>
                ))}
            {r.last_scan_at && (
                <span>
                    last scan=
                    <span className="text-text-muted">
                        {new Date(r.last_scan_at).toLocaleString()}
                    </span>
                </span>
            )}
        </div>
    </div>
);

// ============== Phase E — public_audit result renderer ==================== //

const publicAuditSeverity = (sev) => {
    if (sev === "critical") return "bg-accent-red/15 text-accent-red border-accent-red/40";
    if (sev === "high") return "bg-accent-amber/15 text-accent-amber border-accent-amber/40";
    if (sev === "medium") return "bg-yellow-500/15 text-yellow-400 border-yellow-500/30";
    return "bg-accent-mint/15 text-accent-mint border-accent-mint/40";
};

const PublicAuditResult = ({ r }) => {
    if (!r) return null;
    // RATE_LIMITED outcome — render as a friendly upgrade nudge.
    if (r.error_code === "RATE_LIMITED" || r.error === "RATE_LIMITED") {
        return (
            <div className="rounded border border-accent-amber/30 bg-accent-amber/[0.06] px-3 py-2.5 text-[12.5px]" data-testid="result-public-audit-rate-limited">
                <div className="inline-flex items-center gap-1.5 text-accent-amber font-mono uppercase tracking-wider text-[11px] mb-1">
                    <Sparkles className="w-3 h-3" />
                    Free quota used
                </div>
                <p className="text-text-primary leading-relaxed">
                    {r.user_message || "You've used your free Public Surface Audit for the day."}
                </p>
                <Link to="/pricing" className="inline-flex items-center gap-1 mt-2 text-[11.5px] text-accent-cyan hover:underline">
                    View Pro plans <ExternalLink className="w-2.5 h-2.5" />
                </Link>
            </div>
        );
    }

    const subs = Array.isArray(r.preview_subdomains) ? r.preview_subdomains : [];
    return (
        <div className="space-y-2" data-testid="result-public-audit">
            <div className="grid grid-cols-3 gap-2">
                <div className={`rounded border px-2 py-1.5 text-center ${publicAuditSeverity(r.severity)}`}>
                    <div className="text-[10.5px] font-mono uppercase tracking-wider opacity-75">Score</div>
                    <div className="text-[14px] font-mono">{r.risk_score ?? "—"}/100</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-90 mt-0.5">{r.severity || "—"}</div>
                </div>
                <div className="rounded border border-white/[0.06] bg-elevated/40 px-2 py-1.5 text-center">
                    <div className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">Subdomains</div>
                    <div className="text-[14px] font-mono text-accent-cyan">{r.subdomains_count ?? 0}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-text-dim mt-0.5">via crt.sh</div>
                </div>
                <div className="rounded border border-white/[0.06] bg-elevated/40 px-2 py-1.5 text-center">
                    <div className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">Resolving</div>
                    <div className="text-[14px] font-mono text-accent-mint">{r.subdomains_resolving ?? 0}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-text-dim mt-0.5">A/AAAA live</div>
                </div>
            </div>

            {Array.isArray(r.reasons) && r.reasons.length > 0 && (
                <ul className="space-y-1 text-[12px] text-text-primary leading-snug">
                    {r.reasons.slice(0, 4).map((reason, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                            <ShieldAlert className="w-3 h-3 text-accent-amber shrink-0 mt-0.5" />
                            <span>{reason}</span>
                        </li>
                    ))}
                </ul>
            )}

            {subs.length > 0 && (
                <div className="rounded border border-white/[0.06] overflow-hidden">
                    <div className="px-2 py-1 bg-elevated/40 text-[10.5px] font-mono uppercase tracking-wider text-text-dim">
                        Top {subs.length} subdomain{subs.length === 1 ? "" : "s"}
                    </div>
                    <ul className="divide-y divide-white/[0.04]">
                        {subs.map((s, i) => (
                            <li key={s.subdomain || i} className="px-2 py-1 text-[12px] font-mono flex items-center justify-between gap-2">
                                <span className="text-accent-cyan truncate">{s.subdomain}</span>
                                <span className="text-text-dim shrink-0 inline-flex items-center gap-1">
                                    {s.resolves ? (
                                        <CheckCircle2 className="w-3 h-3 text-accent-mint" />
                                    ) : (
                                        <XCircle className="w-3 h-3 text-text-dim" />
                                    )}
                                    {Array.isArray(s.ips) && s.ips[0] ? s.ips[0] : "—"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {r.view_full_url && (
                <Link
                    to={r.view_full_url}
                    className="inline-flex items-center gap-1 text-[11.5px] text-accent-cyan hover:underline"
                    data-testid="result-public-audit-permalink"
                >
                    Open full report <ExternalLink className="w-2.5 h-2.5" />
                </Link>
            )}
        </div>
    );
};

export default ToolCallCard;
